import { TABLE_ROWS_PER_PAGE_LIMIT } from '../../common/ui/constants';
import type { Priority, PriorityScheme, Project } from '../../common/ui/types';
import { fetchWithStatus } from '../utils';
import type { PrioritySchemeFilters, PrioritySchemesServiceData } from './types';

const PRIORITY_SCHEMES_API = '/rest/api/2/priorityscheme';

const toPriority = (rawData: unknown): Priority => {
	if (
		!rawData ||
		typeof rawData !== 'object' ||
		!('id' in rawData) ||
		typeof rawData.id !== 'string' ||
		!('name' in rawData) ||
		typeof rawData.name !== 'string' ||
		!('isDefault' in rawData) ||
		typeof rawData.isDefault !== 'boolean' ||
		!('iconUrl' in rawData) ||
		typeof rawData.iconUrl !== 'string' ||
		!('sequence' in rawData) ||
		!(typeof rawData.sequence === 'number' || typeof rawData.sequence === 'string')
	) {
		throw new Error('Unexpected shape of Priority');
	}

	const { id, name, isDefault, iconUrl } = rawData;

	return {
		id,
		name,
		isDefault,
		iconUrl,
		sequence: Number(rawData.sequence),
	};
};

const toProject = (rawData: unknown): Project => {
	if (
		!rawData ||
		typeof rawData !== 'object' ||
		!('id' in rawData) ||
		typeof rawData.id !== 'string' ||
		!('name' in rawData) ||
		typeof rawData.name !== 'string' ||
		!('avatarUrls' in rawData) ||
		typeof rawData.avatarUrls !== 'object' ||
		!rawData.avatarUrls ||
		!('48x48' in rawData.avatarUrls) ||
		typeof rawData.avatarUrls['48x48'] !== 'string'
	) {
		throw new Error('Unexpected shape of Project');
	}

	return {
		id: rawData.id,
		name: rawData.name,
		avatarUrl: rawData.avatarUrls['48x48'],
	};
};

const toPriorityScheme = (rawData: unknown): PriorityScheme => {
	if (
		!rawData ||
		typeof rawData !== 'object' ||
		!('id' in rawData) ||
		typeof rawData.id !== 'string' ||
		!('name' in rawData) ||
		typeof rawData.name !== 'string' ||
		!('projects' in rawData) ||
		typeof rawData.projects !== 'object' ||
		!rawData.projects ||
		!('total' in rawData.projects) ||
		typeof rawData.projects.total !== 'number' ||
		!('values' in rawData.projects) ||
		!Array.isArray(rawData.projects.values) ||
		!('priorities' in rawData) ||
		typeof rawData.priorities !== 'object' ||
		!rawData.priorities ||
		!('values' in rawData.priorities) ||
		!Array.isArray(rawData.priorities.values)
	) {
		throw new Error('Unexpected shape of PriorityScheme');
	}

	const {
		id,
		name,
		priorities: { values: rawPriorities },
		projects: { total, values: rawProjects },
	} = rawData;

	// description is optional on the server, but we don't want to deal with undefined
	const description =
		'description' in rawData && typeof rawData.description === 'string' ? rawData.description : '';

	return {
		id,
		name,
		description,
		isDefault: 'isDefault' in rawData ? !!rawData.isDefault : false,
		priorities: rawPriorities.map(toPriority),
		projects: {
			total,
			values: rawProjects.map(toProject),
		},
	};
};

export const toPrioritySchemesData = (rawData: unknown): PrioritySchemesServiceData => {
	if (
		!rawData ||
		typeof rawData !== 'object' ||
		!('total' in rawData) ||
		typeof rawData.total !== 'number' ||
		!('values' in rawData) ||
		!Array.isArray(rawData.values)
	) {
		throw new Error('Unexpected response from the server');
	}

	const { total, values } = rawData;

	return {
		prioritySchemes: values.map(toPriorityScheme),
		prioritySchemesCount: total,
		taskInProgress: 'task' in rawData,
	};
};

export const filterToQueryParams = (filter: PrioritySchemeFilters): URLSearchParams => {
	const searchParams = new URLSearchParams();

	filter.priorityIds.forEach((priorityId) => searchParams.append('priorityId', priorityId));
	if (filter.schemeName) {
		searchParams.append('schemeName', filter.schemeName);
	}

	return searchParams;
};

export const emptyFilter: PrioritySchemeFilters = {
	priorityIds: [],
	schemeName: '',
};

export function createSearchParams(
	page: number,
	filters: PrioritySchemeFilters,
	onlyDefault = false,
): URLSearchParams {
	const searchParams = filterToQueryParams(filters);

	searchParams.append('expand', 'priorities,projects,taskInProgress');
	searchParams.append('maxResults', String(TABLE_ROWS_PER_PAGE_LIMIT));
	searchParams.append('onlyDefault', onlyDefault ? 'true' : 'false');

	const startAtParam = (page - 1) * TABLE_ROWS_PER_PAGE_LIMIT;

	searchParams.append('startAt', String(startAtParam));
	return searchParams;
}

export const fetchPrioritySchemes = async (
	page: number,
	filters: PrioritySchemeFilters,
	onlyDefault = false,
) => {
	const searchParams = createSearchParams(page, filters, onlyDefault);

	const URL = `${PRIORITY_SCHEMES_API}?${searchParams}`;

	const { body, status } = await fetchWithStatus(URL);

	if (status !== 200) {
		throw new Error(`Unexpected response status: ${status} for ${PRIORITY_SCHEMES_API}`);
	}

	return toPrioritySchemesData(body);
};
